import { createContext, Fragment, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { ChevronLeft, ChevronRight, ImageOff, Volume2, VolumeX } from 'lucide-react';
import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useDocViewer } from '../../../../lib/doc-viewer-presenter';
import { AVATAR_PALETTE } from '../../../../lib/avatar-color';
import {
  POST_REEL_RENDITION,
  POST_STORY_RENDITION,
  POST_UNMEASURED_PHOTO,
  POST_UNMEASURED_VIDEO,
  postCaptionDirection,
  postCaptionSegments,
  postCaptionText,
  postFillsTallFrame,
  postInitial,
  postMediaKey,
  postMediaTotal,
  postNaturalAspect,
  postScheduledLine,
  type PostCaptionCut,
  type PostCaptionMarks,
  type PostGridCount,
  type PostMediaGrid,
} from '../../../../lib/social-post-look-parts';
import { socialPostImageRendition, socialPostLogoKey, socialPostVideoSrc, type SocialPostRendition } from '../../../../lib/social-post-preview-card';
import { Avatar, ScrollArea, SocialLogo, TextButton } from '../../ui';
import styles from './SocialPostLookParts.module.css';

export const POST_LOOK_KEYS = {
  video: 'siteReview.element.video',
  image: 'files.type.image',
  place: 'wins.reel.count',
  hidden: 'home.map.moreHidden',
  previous: 'calendar.toolbar.previous',
  next: 'calendar.toolbar.next',
  mute: 'voiceCall.mute',
  unmute: 'voiceCall.unmute',
  showMore: 'chat.socialPost.showMore',
  showLess: 'chat.socialPost.showLess',
  justNow: 'chat.socialPost.justNow',
  subscribe: 'chat.socialPost.subscribe',
  following: 'chat.socialPost.following',
  forYou: 'chat.socialPost.forYou',
  visitSite: 'chat.socialPost.visitSite',
  videoTitle: 'chat.socialPost.videoTitle',
} as const;

const FEED_MARKS: PostCaptionMarks = { hashtags: true, mentions: false, urls: 'marked' };
const ON_MEDIA_MARKS: PostCaptionMarks = { hashtags: false, mentions: false, urls: 'marked' };

export type PostControl =
  | 'social_post_preview_step_next'
  | 'social_post_preview_step_previous'
  | 'social_post_preview_sound_on'
  | 'social_post_preview_sound_off';

export const PostControlContext = createContext<(control: PostControl) => void>(() => undefined);

export interface SocialPostLookProps {
  card: SocialPostPreviewCardMetadata;
  onOpenMedia: (media: SocialPostPreviewMedia, index: number) => void;
  onToggleCaption: (expanded: boolean) => void;
  onOpenLink: () => void;
  onPortrait?: (portrait: boolean) => void;
}

export type MediaLabel = (media: SocialPostPreviewMedia, index: number, hidden?: number) => string;

type MediaGridOf = Extract<PostMediaGrid, { kind: 'grid' }>;

export function useMediaLabel(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount' | 'postType'>): MediaLabel {
  const { t } = useTranslation();
  const total = card.postType === 'story' ? card.media.length : postMediaTotal(card);
  return (media, index, hidden = 0) => {
    const kind = media.kind === 'video' ? t(POST_LOOK_KEYS.video) : t(POST_LOOK_KEYS.image);
    const place = total > 1 ? `${kind} ${t(POST_LOOK_KEYS.place, { index: index + 1, total })}` : kind;
    return hidden > 0 ? `${place}, +${t(POST_LOOK_KEYS.hidden, { count: hidden })}` : place;
  };
}

export interface PostSubtitleClasses {
  part: string;
  pause: string;
}

export function PostSubtitleParts({ parts, classes }: { parts: readonly string[]; classes: PostSubtitleClasses }) {
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <>
              <span className={classes.pause}>,</span>{' '}
            </>
          )}
          <span className={classes.part}>{part}</span>
        </Fragment>
      ))}
    </>
  );
}

export function usePostTiming(card: SocialPostPreviewCardMetadata): { text: string; scheduled: boolean } {
  const { t, i18n } = useTranslation();
  const scheduled = postScheduledLine(card, i18n.language);
  return scheduled ? { text: scheduled, scheduled: true } : { text: t(POST_LOOK_KEYS.justNow), scheduled: false };
}

export function PostTiming({ card }: { card: SocialPostPreviewCardMetadata }) {
  return <>{usePostTiming(card).text}</>;
}

export function PostAvatar({ card, name, size, logoSize }: { card: SocialPostPreviewCardMetadata; name: string; size: number; logoSize?: number }) {
  const [failedUrl, setFailedUrl] = useState<string | undefined>(undefined);
  const pictureUrl = card.accountPictureUrl && card.accountPictureUrl !== failedUrl ? card.accountPictureUrl : null;
  if (logoSize && !pictureUrl && !card.accountName?.trim()) {
    return (
      <span className={styles.logoMark} style={{ width: size, height: size }} aria-hidden="true" data-testid="post-network-logo">
        <SocialLogo network={socialPostLogoKey(card.network)} size={logoSize} />
      </span>
    );
  }
  return (
    <span className={styles.avatar} data-testid="post-avatar">
      <Avatar
        initials={postInitial(name)}
        photoUrl={pictureUrl}
        onPhotoError={() => setFailedUrl(card.accountPictureUrl)}
        size={size}
        color={AVATAR_PALETTE[0]}
      />
    </span>
  );
}

function useCaptionToggle<T extends HTMLElement>(caption: string, onToggle: (expanded: boolean) => void) {
  const [expandedCaption, setExpandedCaption] = useState<string | null>(null);
  const expanded = expandedCaption === caption;
  const ref = useRef<T>(null);
  const focusOnExpand = useRef(false);
  const toggle = () => {
    focusOnExpand.current = !expanded;
    setExpandedCaption(expanded ? null : caption);
    onToggle(!expanded);
  };

  useEffect(() => {
    if (expanded && focusOnExpand.current) {
      focusOnExpand.current = false;
      ref.current?.focus();
    }
  }, [expanded]);

  return { expanded, ref, toggle };
}

export function PostCaption({
  caption,
  className,
  testId,
  cut,
  prefix,
  marks,
  onMedia = false,
  onToggle,
}: {
  caption: string;
  className?: string;
  testId: string;
  cut: PostCaptionCut;
  prefix?: ReactNode;
  marks?: PostCaptionMarks;
  onMedia?: boolean;
  onToggle: (expanded: boolean) => void;
}) {
  const { t } = useTranslation();
  const { expanded, ref, toggle } = useCaptionToggle<HTMLElement>(caption, onToggle);
  const { text, truncated } = postCaptionText(caption, expanded, cut);
  const { hashtags, mentions, urls } = marks ?? (onMedia ? ON_MEDIA_MARKS : FEED_MARKS);
  const paragraphs = text.split('\n');
  const toggleButton = (expanded || truncated) && (
    <>
      {expanded && ' '}
      <TextButton size="small" className={styles.seeMore} aria-expanded={expanded} onClick={toggle}>
        {expanded ? t(POST_LOOK_KEYS.showLess) : t(POST_LOOK_KEYS.showMore)}
      </TextButton>
    </>
  );
  const body = paragraphs.map((paragraph, at) => {
    const lead = at === 0 && prefix;
    const toggleHere = at === paragraphs.length - 1 && toggleButton;
    return (
      <Fragment key={at}>
        {at > 0 && '\n'}
        {(paragraph || lead || toggleHere) && (
          <span className={styles.captionText} dir={postCaptionDirection(paragraph) ?? 'auto'} data-testid={`${testId}-text`}>
            {lead && <>{prefix} </>}
            {postCaptionSegments(paragraph, { hashtags: true, mentions: true }).map((segment, index) => {
              if (segment.kind === 'text') {
                return segment.text;
              }
              const marked = segment.kind === 'url' || (segment.text.startsWith('#') ? hashtags : mentions);
              return marked ? (
                <span
                  key={index}
                  className={segment.kind === 'tag' || urls === 'marked' ? styles.highlight : undefined}
                  dir="auto"
                  data-mark={segment.kind}
                >
                  {segment.text}
                </span>
              ) : (
                <span key={index} dir="auto">
                  {segment.text}
                </span>
              );
            })}
            {toggleHere}
          </span>
        )}
      </Fragment>
    );
  });
  const classes = [styles.caption, onMedia && styles.onMediaCaption, className].filter(Boolean).join(' ');
  const shared = { className: classes, tabIndex: -1, 'data-testid': testId, 'data-expanded': expanded };
  return onMedia ? (
    <ScrollArea ref={ref} component="p" {...shared}>
      {body}
    </ScrollArea>
  ) : (
    <p ref={ref as RefObject<HTMLParagraphElement>} {...shared}>
      {body}
    </p>
  );
}

export function BrokenMedia({ covered = false }: { covered?: boolean }) {
  return (
    <span className={styles.placeholder} data-testid="post-media-placeholder">
      {!covered && <ImageOff size={20} aria-hidden="true" />}
    </span>
  );
}

export function StepButtons({
  index,
  total,
  light = false,
  size = 'tiny',
  onStep,
}: {
  index: number;
  total: number;
  light?: boolean;
  size?: 'tiny' | 'small';
  onStep: (index: number) => void;
}) {
  const { t } = useTranslation();
  const onControl = useContext(PostControlContext);
  const backRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const handOff = useRef<RefObject<HTMLButtonElement | null> | null>(null);

  useEffect(() => {
    handOff.current?.current?.focus();
    handOff.current = null;
  }, [index]);

  if (total < 2) {
    return null;
  }
  const step = [styles.step, light && styles.stepLight, size === 'small' && styles.stepSmall].filter(Boolean).join(' ');
  const go = (to: number) => {
    handOff.current = to <= 0 ? nextRef : to >= total - 1 ? backRef : null;
    onControl(to > index ? 'social_post_preview_step_next' : 'social_post_preview_step_previous');
    onStep(to);
  };
  return (
    <>
      {index > 0 && (
        <button
          ref={backRef}
          type="button"
          className={`${step} ${styles.stepBack}`}
          aria-label={t(POST_LOOK_KEYS.previous)}
          onClick={() => go(index - 1)}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
      )}
      {index < total - 1 && (
        <button
          ref={nextRef}
          type="button"
          className={`${step} ${styles.stepNext}`}
          aria-label={t(POST_LOOK_KEYS.next)}
          onClick={() => go(index + 1)}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      )}
    </>
  );
}

export function StoryBars({ total, index }: { total: number; index: number }) {
  return (
    <div className={styles.bars} data-testid="post-story-bars">
      {Array.from({ length: total }, (_, at) => (
        <span key={at} className={at <= index ? `${styles.bar} ${styles.barSeen}` : styles.bar} data-seen={at <= index} />
      ))}
    </div>
  );
}

export function CarouselDots({ count, active, className }: { count: number; active: number; className?: string }) {
  return (
    <div className={className ? `${styles.dots} ${className}` : styles.dots} data-testid="post-carousel-dots" aria-hidden="true">
      {Array.from({ length: count }, (_, at) => (
        <span key={at} className={at === active ? `${styles.dot} ${styles.dotOn}` : styles.dot} data-on={at === active} />
      ))}
    </div>
  );
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => setReduced(prefersReducedMotion()), []);
  return reduced;
}

function useInlineVideo(url: string, onMeasure?: (width: number, height: number) => void, onFail?: () => void, mutedBy?: boolean) {
  const collectionOpen = Boolean(useDocViewer()?.collection);
  const ref = useRef<HTMLVideoElement>(null);
  const [trackedUrl, setTrackedUrl] = useState(url);
  const [failed, setFailed] = useState(false);
  const [ownMuted, setOwnMuted] = useState(true);
  const muted = mutedBy ?? ownMuted;

  if (trackedUrl !== url) {
    setTrackedUrl(url);
    setFailed(false);
    setOwnMuted(true);
  }

  useEffect(() => {
    if (ref.current) {
      ref.current.muted = muted;
    }
  }, [muted, url, failed]);

  useEffect(() => {
    const player = ref.current;
    if (collectionOpen) {
      player?.pause();
      return;
    }
    if (!player || failed || prefersReducedMotion()) {
      return;
    }
    const play = () => {
      try {
        Promise.resolve(player.play()).catch(() => undefined);
      } catch {
        return;
      }
    };
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[entries.length - 1]?.isIntersecting) {
          play();
        } else {
          player.pause();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(player);
    return () => observer.disconnect();
  }, [url, failed, collectionOpen]);

  const videoProps = {
    ref,
    src: socialPostVideoSrc(url),
    loop: true,
    muted: true,
    playsInline: true,
    preload: 'metadata',
    onLoadedMetadata: (event: { currentTarget: HTMLVideoElement }) => onMeasure?.(event.currentTarget.videoWidth, event.currentTarget.videoHeight),
    onError: () => {
      setFailed(true);
      onFail?.();
    },
  };
  return { failed, muted, toggleMute: () => setOwnMuted((value) => !value), videoProps };
}

export function SoundToggle({ muted, className, onToggle }: { muted: boolean; className?: string; onToggle: () => void }) {
  const { t } = useTranslation();
  const onControl = useContext(PostControlContext);
  const toggle = () => {
    onControl(muted ? 'social_post_preview_sound_on' : 'social_post_preview_sound_off');
    onToggle();
  };
  return (
    <button
      type="button"
      className={className ? `${styles.sound} ${className}` : styles.sound}
      aria-label={muted ? t(POST_LOOK_KEYS.unmute) : t(POST_LOOK_KEYS.mute)}
      onClick={toggle}
      data-testid="post-sound"
    >
      {muted ? <VolumeX size={18} aria-hidden="true" /> : <Volume2 size={18} aria-hidden="true" />}
    </button>
  );
}

export function InlineMediaTile({
  media,
  label,
  rendition,
  fit,
  muted,
  soundClassName,
  onMeasure,
  onFail,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition?: SocialPostRendition;
  fit: 'contain' | 'cover';
  muted?: boolean;
  soundClassName?: string;
  onMeasure?: (width: number, height: number) => void;
  onFail?: () => void;
  onOpen: () => void;
}) {
  const [failedImage, setFailedImage] = useState<string | undefined>(undefined);
  const video = useInlineVideo(media.url, onMeasure, onFail, muted);
  const reduced = useReducedMotion();
  const isVideo = media.kind === 'video';
  const failed = isVideo ? video.failed : failedImage === media.url;
  const fill = fit === 'contain' ? styles.contain : styles.fill;
  return (
    <>
      <button type="button" className={styles.inlineTile} aria-label={label} data-kind={media.kind} onClick={onOpen}>
        {failed ? (
          <BrokenMedia />
        ) : isVideo ? (
          <video className={fill} {...video.videoProps} />
        ) : (
          <img
            className={fill}
            src={rendition ? socialPostImageRendition(media.url, rendition) : media.url}
            alt=""
            onLoad={(event) => onMeasure?.(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)}
            onError={() => {
              setFailedImage(media.url);
              onFail?.();
            }}
          />
        )}
      </button>
      {isVideo && !failed && !reduced && muted === undefined && (
        <SoundToggle muted={video.muted} className={soundClassName} onToggle={video.toggleMute} />
      )}
    </>
  );
}

export function TallFrame({ fits, children }: { fits: boolean; children: ReactNode }) {
  return (
    <div className={fits ? `${styles.fullMedia} ${styles.fitsFrame}` : styles.fullMedia} data-fits={fits}>
      {children}
    </div>
  );
}

export function TallMedia({
  media,
  label,
  rendition,
  fit = 'contain',
  muted,
  soundClassName,
  onOpen,
  onFit,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition?: SocialPostRendition;
  fit?: 'contain' | 'cover';
  muted?: boolean;
  soundClassName?: string;
  onOpen: () => void;
  onFit: (fits: boolean) => void;
}) {
  const fitRef = useRef(onFit);
  useEffect(() => {
    fitRef.current = onFit;
  });
  useEffect(() => () => fitRef.current(false), []);
  return (
    <InlineMediaTile
      media={media}
      label={label}
      rendition={rendition}
      fit={fit}
      muted={muted}
      soundClassName={soundClassName}
      onMeasure={(width, height) => fitRef.current(postFillsTallFrame(width, height))}
      onFail={() => fitRef.current(false)}
      onOpen={onOpen}
    />
  );
}

export function PostTile({
  media,
  label,
  rendition,
  hidden = 0,
  onMeasure,
  onFail,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition: SocialPostRendition;
  hidden?: number;
  onMeasure?: (width: number, height: number) => void;
  onFail?: () => void;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const image = useRef<HTMLImageElement>(null);
  const measureRef = useRef(onMeasure);
  useEffect(() => {
    measureRef.current = onMeasure;
  });
  useEffect(() => {
    const img = image.current;
    if (img?.complete && img.naturalWidth > 0) {
      measureRef.current?.(img.naturalWidth, img.naturalHeight);
    }
  }, [media.url]);
  const fail = () => {
    setFailed(true);
    onFail?.();
  };
  return (
    <button type="button" className={styles.tile} aria-label={label} data-kind={media.kind} onClick={onOpen}>
      {failed ? (
        <BrokenMedia covered={hidden > 0} />
      ) : media.kind === 'video' ? (
        <video
          className={styles.fill}
          src={socialPostVideoSrc(media.url)}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={(event) => onMeasure?.(event.currentTarget.videoWidth, event.currentTarget.videoHeight)}
          onError={fail}
        />
      ) : (
        <img
          ref={image}
          className={styles.fill}
          src={socialPostImageRendition(media.url, rendition)}
          alt=""
          loading="lazy"
          onLoad={(event) => onMeasure?.(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)}
          onError={fail}
        />
      )}
      {hidden > 0 && (
        <span className={styles.more} data-testid="post-more">
          +{hidden}
        </span>
      )}
    </button>
  );
}

export function PostSingleMedia({
  media,
  label,
  rendition,
  className,
  testId,
  unmeasured,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition: SocialPostRendition;
  className: string;
  testId: string;
  unmeasured?: number;
  onOpen: () => void;
}) {
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const frame: CSSProperties = {
    aspectRatio: String(aspect ?? unmeasured ?? (media.kind === 'video' ? POST_UNMEASURED_VIDEO : POST_UNMEASURED_PHOTO)),
  };
  const measure = (width: number, height: number) => setAspect(postNaturalAspect(width, height));
  return (
    <div className={className} style={frame} data-testid={testId} data-layout="single" data-failed={failed}>
      {media.kind === 'video' ? (
        <InlineMediaTile media={media} label={label} fit="cover" onMeasure={measure} onFail={() => setFailed(true)} onOpen={onOpen} />
      ) : (
        <PostTile media={media} label={label} rendition={rendition} onMeasure={measure} onFail={() => setFailed(true)} onOpen={onOpen} />
      )}
    </div>
  );
}

export function PostGrid({
  grid,
  label,
  rendition,
  className,
  testId,
  onOpen,
}: {
  grid: MediaGridOf;
  label: MediaLabel;
  rendition: (count: PostGridCount, index: number) => SocialPostRendition;
  className: string;
  testId: string;
  onOpen: (media: SocialPostPreviewMedia, index: number) => void;
}) {
  return (
    <div className={className} data-testid={testId} data-layout="grid" data-count={grid.count}>
      {grid.tiles.map((media, index) => {
        const hidden = index === grid.tiles.length - 1 ? grid.hidden : 0;
        return (
          <PostTile
            key={`${media.url}-${index}`}
            media={media}
            label={label(media, index, hidden)}
            rendition={rendition(grid.count, index)}
            hidden={hidden}
            onOpen={() => onOpen(media, index)}
          />
        );
      })}
    </div>
  );
}

interface MediaCarouselProps {
  card: SocialPostPreviewCardMetadata;
  rendition: SocialPostRendition;
  aspectOf: (width: number, height: number) => number;
  className?: string;
  testId: string;
  lightSteps?: boolean;
  onOpen: (media: SocialPostPreviewMedia, index: number) => void;
}

export function MediaCarousel(props: MediaCarouselProps) {
  return <Carousel key={postMediaKey(props.card)} {...props} />;
}

function Carousel({ card, rendition, aspectOf, className, testId, lightSteps = false, onOpen }: MediaCarouselProps) {
  const { media } = card;
  const label = useMediaLabel(card);
  const [index, setIndex] = useState(0);
  const [shape, setShape] = useState<number | undefined>(undefined);
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const [muted, setMuted] = useState(true);
  const reduced = useReducedMotion();
  const at = Math.min(index, media.length - 1);
  const current = media[at];
  if (!current) {
    return null;
  }
  const sharedSound = media.length > 1 && media.some((item) => item.kind === 'video');
  const frame: CSSProperties = { aspectRatio: String(shape ?? aspectOf(0, 0)) };
  const measure = at === 0 ? (width: number, height: number) => setShape(aspectOf(width, height)) : undefined;
  const fail = () => setFailed((known) => ({ ...known, [current.url]: true }));
  const open = () => onOpen(current, at);
  return (
    <div
      className={className ? `${styles.frame} ${className}` : styles.frame}
      style={frame}
      data-testid={testId}
      data-layout={media.length > 1 ? 'carousel' : 'single'}
      data-failed={!!failed[current.url]}
      data-index={at}
    >
      {current.kind === 'video' ? (
        <InlineMediaTile
          key={`${at}-${current.url}`}
          media={current}
          label={label(current, at)}
          fit="cover"
          muted={sharedSound ? muted : undefined}
          onMeasure={measure}
          onFail={fail}
          onOpen={open}
        />
      ) : (
        <PostTile
          key={`${at}-${current.url}`}
          media={current}
          label={label(current, at)}
          rendition={rendition}
          onMeasure={measure}
          onFail={fail}
          onOpen={open}
        />
      )}
      <StepButtons index={at} total={media.length} light={lightSteps} onStep={setIndex} />
      {sharedSound && current.kind === 'video' && !reduced && <SoundToggle muted={muted} onToggle={() => setMuted((value) => !value)} />}
    </div>
  );
}

interface TallPostProps extends SocialPostLookProps {
  kind: 'reel' | 'story';
  name: string;
  nameTestId: string;
  frameTestId: string;
  captionTestId?: string;
  captionLimit?: number;
  marks?: PostCaptionMarks;
}

export function TallPost(props: TallPostProps) {
  return props.kind === 'reel' ? <TallReel {...props} /> : <TallStory key={postMediaKey(props.card)} {...props} />;
}

function TallReel({ card, name, nameTestId, frameTestId, captionTestId = '', captionLimit = 0, marks, onOpenMedia, onToggleCaption }: TallPostProps) {
  const label = useMediaLabel(card);
  const [fits, setFits] = useState(false);
  const media = card.media[0];
  if (!media) {
    return null;
  }
  return (
    <div className={`${styles.tallPost} ${styles.reel}`} data-testid={frameTestId}>
      <TallFrame fits={fits}>
        <TallMedia
          key={media.url}
          media={media}
          label={label(media, 0)}
          rendition={POST_REEL_RENDITION}
          soundClassName={styles.reelSound}
          onOpen={() => onOpenMedia(media, 0)}
          onFit={setFits}
        />
        <div className={styles.reelOverlay}>
          <div className={styles.onMediaName}>
            <PostAvatar card={card} name={name} size={24} />
            <span className={styles.reelName} data-testid={nameTestId}>
              <bdi>{name}</bdi>
            </span>
          </div>
          {card.caption && (
            <PostCaption
              caption={card.caption}
              className={styles.reelCaption}
              testId={captionTestId}
              cut={{ limit: captionLimit }}
              marks={marks}
              onMedia
              onToggle={onToggleCaption}
            />
          )}
        </div>
      </TallFrame>
    </div>
  );
}

function TallStory({ card, name, nameTestId, frameTestId, onOpenMedia }: TallPostProps) {
  const items = card.media;
  const label = useMediaLabel(card);
  const [index, setIndex] = useState(0);
  const [fits, setFits] = useState(false);
  const [muted, setMuted] = useState(true);
  const reduced = useReducedMotion();
  const current = items[Math.min(index, items.length - 1)];
  if (!current) {
    return null;
  }
  return (
    <div className={`${styles.tallPost} ${styles.story}`} data-testid={frameTestId}>
      <TallFrame fits={fits}>
        <TallMedia
          key={`${index}-${current.url}`}
          media={current}
          label={label(current, index)}
          rendition={POST_STORY_RENDITION}
          muted={muted}
          onOpen={() => onOpenMedia(current, index)}
          onFit={setFits}
        />
        <div className={styles.storyTop}>
          <StoryBars total={items.length} index={index} />
          <div className={styles.storyHead}>
            <PostAvatar card={card} name={name} size={24} />
            <span className={styles.storyName} data-testid={nameTestId}>
              <bdi>{name}</bdi>
            </span>
          </div>
        </div>
        <StepButtons index={index} total={items.length} onStep={setIndex} />
        {current.kind === 'video' && !reduced && <SoundToggle muted={muted} onToggle={() => setMuted((value) => !value)} />}
      </TallFrame>
    </div>
  );
}
